import React, {useCallback, useEffect, useState} from 'react';
import {plural, getPurchaseOrdersMissingDeliveryNote} from './utils';
import {
  IFarmInputInvoice,
  IFarmInputOrder,
  IFarmInputProduct,
  IFarmInputPurchaseOrder,
  IInputMessage,
  ISupplierUserProfile,
  ISupportingDocumentInput,
} from 'constants/interfaces';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import AddIcon from '@material-ui/icons/Add';
import Hidden, {HiddenProps} from '@material-ui/core/Hidden';
import {Route} from 'react-router-dom';
import moment, {Moment} from 'moment';
import GetAppIcon from '@material-ui/icons/GetApp';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import AssignmentIcon from '@material-ui/icons/Assignment';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';

import {NavigationService} from 'services/index';
import {PermissionsService} from 'services/PermissionsService';
import {
  EPurchaseOrderSupportingDocs,
  ESupplierUserPermissions,
} from 'constants/index';
import {
  apiSavePurchaseOrder,
  apiAddProformaInvoiceProduct,
  apiConvertProformaToInvoice,
  apiDeleteInvoiceSupportingDocument,
  apiRequestInvoiceDocument,
  apiGetDepotOrder,
  apiRemoveProformaInvoiceProduct,
  apiReplaceProformaInvoiceProducts,
  apiGetInputProducts,
  apiGetInvoiceSupportingDocuments,
  apiGETORDERMESSAGES,
  apiGetPurchaseOrdersByInvoice,
  apiSaveInvoiceSupportingDocument,
  apiSENDTEXTORDERMESSAGE,
} from 'constants/Api';
import type {ReplaceProformaInvoiceLineItemInput} from 'constants/Api';
import {reduxAddPurchaseOrder, reduxSetProducts} from 'appredux/actions';
import {ProductState} from 'appredux/reducers/reducer-products';
import {useSocketEvents} from 'appsockets/SocketEventProvider';

import {FarmerCRMCard} from 'screens/Dashboard/Invoices/FarmerDetailCard/FarmerCRMCard';
import {InvoiceProducts} from 'screens/Dashboard/Invoices/InvoiceProducts/InvoiceProducts';
import {InvoiceDetailCard} from 'screens/Dashboard/Invoices/InvoiceDetailCard/InvoiceDetailCard';
import {InvoiceLogisticsCard} from 'screens/Dashboard/Invoices/InvoiceLogisticsCard/InvoiceLogisticsCard';
import {InvoiceStatusHistoryCard} from 'screens/Dashboard/Invoices/InvoiceStatusHistoryCard/InvoiceStatusHistoryCard';
import {InvoiceChat} from 'screens/Dashboard/Invoices/InvoiceChat/InvoiceChat';
import {SupportingDocuments} from 'components/SupportingDocuments/SupportingDocuments';

import 'screens/Dashboard/Invoices/Invoices.scss';
import './OrderDetail.scss';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';

type MyHiddenProps = HiddenProps & {children?: React.ReactNode};
const MyHidden: React.FC<MyHiddenProps> = ({children, ...rest}) => {
  return <Hidden {...rest}>{children}</Hidden>;
};

export const OrderDetail = (props: {
  user: ISupplierUserProfile;
  invoiceId: number;
  productState: ProductState;
  width: Breakpoint;
  /** When provided (e.g. from OrderDetailPage), skip initial fetch and use this data */
  initialInvoice?: IFarmInputInvoice;
  /** When provided, called after convert-to-invoice so the page can refresh instead of navigating */
  onConvertedToInvoice?: () => void;
}) => {
  const {user, invoiceId, initialInvoice, onConvertedToInvoice} = props;

  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [invoice, setInvoice] = useState<IFarmInputInvoice | undefined>(
    initialInvoice,
  );
  const [isLoadingAllProducts, setIsLoadingAllProducts] = useState(false);
  const [isLoadingPurchaseOrder, setIsLoadingPurchaseOrder] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<
    IFarmInputPurchaseOrder[]
  >([]);
  const [
    showCreatePurchaseOrderConfirmation,
    setShowCreatePurchaseOrderConfirmation,
  ] = useState<boolean>(false);
  const [showAddProductDialog, setShowAddProductDialog] =
    useState<boolean>(false);
  const [isConvertingToInvoice, setIsConvertingToInvoice] =
    useState<boolean>(false);
  const [showConvertToInvoiceConfirm, setShowConvertToInvoiceConfirm] =
    useState<boolean>(false);

  const missingDeliveryNotePOs =
    getPurchaseOrdersMissingDeliveryNote(purchaseOrders);
  const allPOsHaveDeliveryNotes = missingDeliveryNotePOs.length === 0;

  const [tabIndex, setTabIndex] = useState(0);
  const showTabs = props.width === 'xs' || props.width === 'sm';

  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isRefreshingMessages, setIsRefreshingMessages] = useState(false);
  const [messages, setMessages] = useState<IInputMessage[]>([]);

  const {newInvoiceMessage} = useSocketEvents();

  const loadInvoice = useCallback((invId: number) => {
    setIsLoadingInvoice(true);
    apiGetDepotOrder(invId)
      .then(setInvoice)
      .catch((error) => {
        alert('Error loading order. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingInvoice(false));
  }, []);

  useEffect(() => {
    if (initialInvoice != null) {
      setInvoice(initialInvoice);
      return;
    }
    loadInvoice(invoiceId);
  }, [invoiceId, initialInvoice, loadInvoice]);

  const loadPurchaseOrder = useCallback((invId: number) => {
    setIsLoadingPurchaseOrder(true);
    apiGetPurchaseOrdersByInvoice(invId)
      .then(setPurchaseOrders)
      .catch((error) => {
        alert('Error loading purchase order. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingPurchaseOrder(false));
  }, []);

  useEffect(() => {
    loadPurchaseOrder(invoiceId);
  }, [invoiceId, loadPurchaseOrder]);

  const loadMessages = useCallback((invId: number) => {
    setIsLoadingMessages(true);
    apiGETORDERMESSAGES(invId)
      .then(setMessages)
      .catch((error) => {
        alert('Error loading messages. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingMessages(false));
  }, []);

  const refreshMessages = useCallback((invId: number) => {
    setIsRefreshingMessages(true);
    apiGETORDERMESSAGES(invId)
      .then(setMessages)
      .catch((error) => {
        alert('Error loading messages. Please try again later');
        console.error(error);
      })
      .finally(() => setIsRefreshingMessages(false));
  }, []);

  const handleTextMessageSend = useCallback(
    (invId: number, text: string) => {
      apiSENDTEXTORDERMESSAGE(invId, text)
        .then((response) => {
          if (response.success) refreshMessages(invId);
          else throw new Error(response.message);
        })
        .catch((error) => {
          alert('Error sending message');
          console.error(error);
        });
    },
    [refreshMessages],
  );

  useEffect(() => {
    loadMessages(invoiceId);
  }, [invoiceId, loadMessages]);

  useEffect(() => {
    if (newInvoiceMessage?.invoice.id === invoiceId) {
      refreshMessages(invoiceId);
    }
  }, [newInvoiceMessage, invoiceId, refreshMessages]);

  const loadAllProducts = useCallback(() => {
    const {user, productState} = props;
    const {lastUpdate, products} = productState;

    const productsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(8, 'hours'),
    );
    const noProducts = !products?.length;

    if (productsAreExpired || noProducts) {
      setIsLoadingAllProducts(true);
      apiGetInputProducts(user)
        .then((fetchedProducts) => reduxSetProducts(fetchedProducts))
        .catch((error) => {
          alert('Error loading products. Please try again later');
          console.error(error);
        })
        .finally(() => setIsLoadingAllProducts(false));
    }
  }, [props]);

  useEffect(loadAllProducts, [loadAllProducts]);

  const handleOrderUpdates = (updatedOrders: IFarmInputOrder[]) => {
    if (!invoice?.orders?.length) return;

    const updatedById = new Map(updatedOrders.map((o) => [String(o.id), o]));
    const mergedOrders = invoice.orders.map((order) => {
      const updated = updatedById.get(String(order.id));
      return updated ?? order;
    });

    const products: ReplaceProformaInvoiceLineItemInput[] = mergedOrders.map(
      (order) => {
        const item: ReplaceProformaInvoiceLineItemInput = {
          stockCode: order.product?.stockCode ?? '',
          price: Number(order.price),
          quantity: Number(order.quantity),
        };
        if (order.id) item.id = String(order.id);
        return item;
      },
    );

    apiReplaceProformaInvoiceProducts(String(invoiceId), products)
      .then(() => loadInvoice(invoiceId))
      .catch((error) => {
        alert('Error updating order. Please try again.');
        console.error(error);
      });
  };

  const handleOrderRemove = (order: IFarmInputOrder) => {
    const stockCode = order.product?.stockCode;
    if (!stockCode) return;
    apiRemoveProformaInvoiceProduct(String(invoiceId), stockCode)
      .then(() => loadInvoice(invoiceId))
      .catch((error) => {
        alert('Error removing product. Please try again.');
        console.error(error);
      });
  };

  const handleDownload = () => {
    if (!invoice) return;
    setIsDownloadingInvoice(true);
    apiRequestInvoiceDocument(invoice.id)
      .then((url) => createCacheAvoidingUrl(url))
      .then((url) => {
        const newWindow = window.open(url, '_blank');
        if (newWindow) newWindow.opener = null;
      })
      .catch((err) => {
        alert('Failed to download invoice. Please try again later');
        console.error('Failed to download invoice', err);
      })
      .finally(() => setIsDownloadingInvoice(false));
  };

  const createCacheAvoidingUrl = (fileUrl: string) => {
    const searchParamToAvoidCache = `date=${new Date().getTime()}`;
    const appendChar = fileUrl.includes('?') ? '&' : '?';
    return `${fileUrl}${appendChar}${searchParamToAvoidCache}`;
  };

  const handleConvertToInvoice = () => {
    setShowConvertToInvoiceConfirm(false);
    setIsConvertingToInvoice(true);
    apiConvertProformaToInvoice(invoiceId)
      .then((result) => {
        if (onConvertedToInvoice) {
          onConvertedToInvoice();
        } else {
          NavigationService.navigate('/orders/' + result.id);
        }
      })
      .catch(() => {
        setIsConvertingToInvoice(false);
      });
  };

  const activeProducts = props.productState.products.filter(
    (product) => product.isActive,
  );

  const loadSupportingDocuments = useCallback(() => {
    return apiGetInvoiceSupportingDocuments(invoiceId);
  }, [invoiceId]);

  const updateSupportingDocument = useCallback(
    (document: ISupportingDocumentInput) => {
      return apiSaveInvoiceSupportingDocument({invoiceId, ...document}).then(
        () => undefined,
      );
    },
    [invoiceId],
  );

  const removeSupportingDocument = useCallback(
    (documentId: string | number) => {
      return apiDeleteInvoiceSupportingDocument(documentId).then(
        () => undefined,
      );
    },
    [],
  );

  const [permissions] = useState(() => new PermissionsService(user));
  const canWriteOrder = permissions.hasPermission(
    ESupplierUserPermissions.ORDER_WRITE,
  );
  const canWriteQuote = permissions.hasPermission(
    ESupplierUserPermissions.QUOTE_WRITE,
  );
  const canWriteDocs = permissions.hasPermission(
    ESupplierUserPermissions.ORDER_MANAGE_DOCUMENTS,
  );
  const canReadChat = permissions.hasPermission(
    ESupplierUserPermissions.ORDER_CHAT_READ,
  );
  const canWriteChat = permissions.hasPermission(
    ESupplierUserPermissions.ORDER_CHAT_WRITE,
  );
  const canReadAndWritePurchaseOrders = user.isAECI;

  const handleViewPurchaseOrder = () => {
    if (purchaseOrders.length === 1) {
      NavigationService.navigate('/purchase-orders/' + purchaseOrders[0].id);
    } else {
      NavigationService.navigate('/purchase-orders?invoiceId=' + invoiceId);
    }
  };

  return (
    <div className="order-detail">
      <AppBar className="app-bar" elevation={0}>
        <Toolbar>
          <Route
            render={({history}) => (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() =>
                  history.location.key
                    ? history.goBack()
                    : history.replace('/orders')
                }
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          />
          <Typography variant="h6" className="title">
            Order {invoice?.invoiceNumber}
          </Typography>

          {invoice && (
            <Tooltip title="Download Proforma" aria-label="download">
              <IconButton
                edge="start"
                color="inherit"
                onClick={!isDownloadingInvoice ? handleDownload : () => null}
                aria-label="download"
              >
                {isDownloadingInvoice ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <GetAppIcon />
                )}
              </IconButton>
            </Tooltip>
          )}

          <MyHidden xsDown>
            {canWriteQuote ? (
              <Tooltip
                title={
                  !allPOsHaveDeliveryNotes
                    ? `Awaiting delivery notes on PO${
                        missingDeliveryNotePOs.length > 1 ? 's' : ''
                      }: ${missingDeliveryNotePOs
                        .map((po) => po.id)
                        .join(', ')}`
                    : 'Promote to invoice'
                }
                aria-label="promote to invoice"
              >
                <span>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={() => setShowConvertToInvoiceConfirm(true)}
                    disabled={
                      isConvertingToInvoice ||
                      !!invoice?.documentUrl ||
                      !allPOsHaveDeliveryNotes
                    }
                    aria-label="promote to invoice"
                    style={{marginLeft: 4}}
                  >
                    {isConvertingToInvoice ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <LocalShippingIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </MyHidden>

          {canReadAndWritePurchaseOrders &&
            !isLoadingPurchaseOrder &&
            purchaseOrders.length === 0 && (
              <Tooltip
                title="Create Purchase Order"
                aria-label="create purchase order"
              >
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => setShowCreatePurchaseOrderConfirmation(true)}
                  aria-label="create purchase order"
                  style={{marginLeft: 4}}
                >
                  <NoteAddIcon />
                </IconButton>
              </Tooltip>
            )}
          {canReadAndWritePurchaseOrders &&
            !isLoadingPurchaseOrder &&
            purchaseOrders.length > 0 && (
              <Tooltip
                title="View Purchase Order"
                aria-label="view purchase order"
              >
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={handleViewPurchaseOrder}
                  aria-label="view purchase order"
                  style={{marginLeft: 4}}
                >
                  <AssignmentIcon />
                </IconButton>
              </Tooltip>
            )}
        </Toolbar>
      </AppBar>

      <AppBar
        position="relative"
        elevation={0}
        variant="outlined"
        className="tabs-container"
        style={{display: showTabs ? '' : 'none'}}
      >
        <Tabs
          value={tabIndex}
          onChange={(event: React.ChangeEvent<{}>, newIndex: number) =>
            setTabIndex(newIndex)
          }
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value={0} label="Details" />
          <Tab value={1} label="Chat" />
        </Tabs>
      </AppBar>

      <Grid container className="container">
        <Grid
          item
          xs={12}
          md={8}
          className="grid-column order-detail-details-column"
          style={{display: showTabs && tabIndex === 1 ? 'none' : ''}}
        >
          <Paper className="invoice-info-container" variant="outlined">
            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Order Details</Typography>
              </Box>
              <InvoiceDetailCard
                isLoading={isLoadingInvoice}
                invoice={invoice}
                user={user}
              />
            </Box>

            <Box className="farm-details-container">
              <FarmerCRMCard
                isLoading={isLoadingInvoice}
                farmer={invoice?.farmer}
                user={user}
                onSave={() => loadInvoice(invoiceId)}
              />
            </Box>

            <Box className="order-products-container-2">
              <Box
                className="header-container"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Typography variant="subtitle1">Line Items</Typography>
                {canWriteOrder && (
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddProductDialog(true)}
                    disabled={!invoice || !!invoice.documentUrl}
                  >
                    Add product
                  </Button>
                )}
              </Box>
              <InvoiceProducts
                isLoading={isLoadingInvoice || isLoadingAllProducts}
                invoice={invoice}
                allInputProducts={activeProducts}
                onOrderUpdate={handleOrderUpdates}
                onOrderRemove={handleOrderRemove}
                readonly={!canWriteOrder || !!invoice?.documentUrl}
              />
            </Box>

            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Logistics</Typography>
              </Box>
              <InvoiceLogisticsCard
                isLoading={isLoadingInvoice}
                invoice={invoice}
              />
            </Box>

            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Operation Progress</Typography>
              </Box>
              <InvoiceStatusHistoryCard
                isLoading={isLoadingInvoice}
                invoice={invoice}
              />
            </Box>

            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">
                  Supporting Documents
                </Typography>
              </Box>
              <SupportingDocuments
                readonly={!canWriteDocs || !!invoice?.documentUrl}
                loadSupportingDocuments={loadSupportingDocuments}
                updateSupportingDocument={updateSupportingDocument}
                removeSupportingDocument={removeSupportingDocument}
                titleOptions={[
                  EPurchaseOrderSupportingDocs.Waybill,
                  EPurchaseOrderSupportingDocs.DeliveryNote,
                  EPurchaseOrderSupportingDocs.Invoice,
                ]}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          className="grid-column order-detail-chat-column"
          style={{display: showTabs && tabIndex === 0 ? 'none' : ''}}
        >
          {canReadChat && (
            <InvoiceChat
              isLoading={isLoadingMessages}
              isRefreshing={isRefreshingMessages}
              myProfilePicture={user.pictureUrl}
              invoice={invoice}
              messages={messages}
              onMessageSend={(text) => handleTextMessageSend(invoiceId, text)}
              readonly={!canWriteChat}
            />
          )}
        </Grid>
      </Grid>

      <CreatePurchaseOrderFromOrderDialog
        invoiceId={invoiceId}
        isOpen={showCreatePurchaseOrderConfirmation}
        onClose={() => setShowCreatePurchaseOrderConfirmation(false)}
      />
      <Dialog
        open={showConvertToInvoiceConfirm}
        onClose={() =>
          !isConvertingToInvoice && setShowConvertToInvoiceConfirm(false)
        }
      >
        <DialogTitle>Convert to invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Convert this proforma order to an invoice? You will be taken to the
            new invoice.
          </Typography>
          {missingDeliveryNotePOs.length > 0 && (
            <Typography color="error" style={{marginTop: 8}}>
              Missing delivery note{plural(missingDeliveryNotePOs.length)} on PO
              {plural(missingDeliveryNotePOs.length)}:{' '}
              {missingDeliveryNotePOs.map((po) => po.id).join(', ')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConvertToInvoiceConfirm(false)}
            disabled={isConvertingToInvoice}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleConvertToInvoice}
            disabled={isConvertingToInvoice}
          >
            {isConvertingToInvoice ? (
              <CircularProgress size={24} thickness={6} />
            ) : (
              'Convert'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <AddProformaProductDialog
        invoiceId={invoiceId}
        isOpen={showAddProductDialog}
        allInputProducts={activeProducts}
        onClose={() => setShowAddProductDialog(false)}
        onAdded={() => {
          loadInvoice(invoiceId);
          setShowAddProductDialog(false);
        }}
      />
    </div>
  );
};

const CreatePurchaseOrderFromOrderDialog = (props: {
  invoiceId: number;
  isOpen: boolean;
  onClose: VoidFunction;
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [expectedPaymentDate, setExpectedPaymentDate] = useState<Moment | null>(
    null,
  );

  const handleCreatePurchaseOrder = () => {
    setIsCreating(true);
    apiSavePurchaseOrder({
      invoiceId: props.invoiceId,
      expectedPaymentDate: expectedPaymentDate?.toDate(),
    })
      .then((purchaseOrder) => {
        reduxAddPurchaseOrder(purchaseOrder);
        return purchaseOrder;
      })
      .then((purchaseOrder) => {
        setTimeout(() => {
          NavigationService.navigate('/purchase-orders/' + purchaseOrder?.id);
        }, 500);
      })
      .catch((error) => {
        alert('Error creating purchase order. Please try again later');
        console.error(error);
      })
      .finally(() => setIsCreating(false));
  };

  return (
    <Dialog
      className="quote-line-item-remove-confirmation-dialog positive-dialog"
      disableEscapeKeyDown
      open={props.isOpen}
      onClose={props.onClose}
    >
      <DialogTitle className="heading">Create Purchase Order</DialogTitle>
      <DialogContent className="body">
        Are you sure you want to create a purchase order?
      </DialogContent>
      <Box padding={2}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDatePicker
            autoOk
            variant="inline"
            inputVariant="outlined"
            label="Expected Payment Date (Optional)"
            format="DD/MM/yyyy"
            value={expectedPaymentDate}
            onChange={setExpectedPaymentDate}
            fullWidth
          />
        </MuiPickersUtilsProvider>
      </Box>
      <DialogActions>
        <Button className="cancel-button" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          className="accept-button"
          onClick={handleCreatePurchaseOrder}
          disabled={isCreating}
        >
          {isCreating ? (
            <CircularProgress color="primary" size={24} thickness={6} />
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddProformaProductDialog = (props: {
  invoiceId: number;
  isOpen: boolean;
  allInputProducts: IFarmInputProduct[];
  onClose: VoidFunction;
  onAdded: VoidFunction;
}) => {
  const [selectedProduct, setSelectedProduct] =
    useState<IFarmInputProduct | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [price, setPrice] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (props.isOpen) {
      setSelectedProduct(null);
      setQuantity(1);
      setPrice(0);
    }
  }, [props.isOpen]);

  useEffect(() => {
    if (selectedProduct) setPrice(Number(selectedProduct.price) || 0);
  }, [selectedProduct]);

  const handleAdd = () => {
    if (!selectedProduct?.stockCode || quantity <= 0) {
      alert('Please select a product and enter a valid quantity.');
      return;
    }
    setIsSubmitting(true);
    apiAddProformaInvoiceProduct(props.invoiceId, {
      stockCode: selectedProduct.stockCode,
      price: Number(price),
      quantity: Number(quantity),
    })
      .then(() => props.onAdded())
      .catch((error) => {
        alert('Error adding product. Please try again.');
        console.error(error);
      })
      .finally(() => setIsSubmitting(false));
  };

  const productOptions = props.allInputProducts.sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return (
    <Dialog
      className="positive-dialog"
      open={props.isOpen}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add product</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gridGap={16} pt={1}>
          <Autocomplete
            options={productOptions}
            getOptionLabel={(product: IFarmInputProduct) => product.name}
            value={selectedProduct}
            onChange={(_, value) => setSelectedProduct(value ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                variant="outlined"
                size="small"
              />
            )}
          />
          <TextField
            label="Quantity"
            type="number"
            variant="outlined"
            size="small"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            inputProps={{min: 1, step: 1}}
          />
          <TextField
            label="Unit price (R)"
            type="number"
            variant="outlined"
            size="small"
            value={price}
            onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
            inputProps={{min: 0, step: 0.01}}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleAdd}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              <AddIcon />
            )
          }
        >
          {isSubmitting ? 'Adding…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
